// QZ Tray Print Service - Impressão direta em impressoras térmicas
import qz from 'qz-tray';

let connected = false;
let connectionPromise = null;

// Override certificate and signature for unsigned/demo mode
qz.security.setCertificatePromise(function(resolve) {
  resolve("");
});

qz.security.setSignaturePromise(function() {
  return function(resolve) {
    resolve("");
  };
});

/**
 * Connect to QZ Tray (local websocket)
 */
export async function connectQZ() {
  if (connected && qz.websocket.isActive()) {
    return true;
  }

  if (connectionPromise) {
    return connectionPromise;
  }

  connectionPromise = qz.websocket.connect({ retries: 3, delay: 1 })
    .then(() => {
      connected = true;
      connectionPromise = null;
      console.log('QZ Tray conectado com sucesso');
      return true;
    })
    .catch((err) => {
      connected = false;
      connectionPromise = null;
      console.error('Erro ao conectar QZ Tray:', err);
      throw new Error(
        'Não foi possível conectar ao QZ Tray. Verifique se o programa está instalado e rodando no seu computador.\n\nBaixe em: https://qz.io/download'
      );
    });

  return connectionPromise;
}

/**
 * Disconnect from QZ Tray
 */
export async function disconnectQZ() {
  if (connected && qz.websocket.isActive()) {
    await qz.websocket.disconnect();
    connected = false;
  }
}

/**
 * Find printers matching a name pattern
 */
export async function findPrinters(searchName = '') {
  await connectQZ();
  
  if (searchName) {
    const printer = await qz.printers.find(searchName);
    return Array.isArray(printer) ? printer : [printer];
  }
  
  return await qz.printers.find();
}

/**
 * Find Argox printer specifically
 */
export async function findArgoxPrinter() {
  await connectQZ();
  
  try {
    const allPrinters = await qz.printers.find();
    const argoxPrinter = allPrinters.find(p => {
      const lower = p.toLowerCase();
      return lower.includes('argox') || 
        lower.includes('os-2140') ||
        lower.includes('os 2140') ||
        lower.includes('ppla') ||
        lower.includes('pplb') ||
        lower.includes('tspl');
    });
    
    return argoxPrinter || null;
  } catch (err) {
    console.error('Erro ao buscar impressora:', err);
    throw err;
  }
}

/**
 * PIXEL PRINTING - Sends label as image through the printer driver.
 * This is the RECOMMENDED method for Argox printers in PPLA mode.
 * Works like BarTender: the driver handles converting the image to printer commands.
 * 
 * @param {string} printerName - Exact Windows printer name (e.g., "Argox OS-2140 ppla")
 * @param {string[]} imagesBase64 - Array of base64-encoded PNG images (one per label)
 * @param {object} labelSize - { width: mm, height: mm }
 */
export async function printPixelLabels(printerName, imagesBase64, labelSize = { width: 40, height: 25 }, dpi = 203) {
  await connectQZ();
  
  // Configure for exact label size using the printer driver
  // density matches the image DPI for 1:1 pixel mapping
  const config = qz.configs.create(printerName, {
    size: { width: labelSize.width / 25.4, height: labelSize.height / 25.4 },
    units: 'in',
    colorType: 'grayscale',
    interpolation: 'nearest-neighbor',
    scaleContent: true,
    rasterize: true,
    density: dpi,
    margins: { top: 0, right: 0, bottom: 0, left: 0 }
  });
  
  // Build data array - one image per label
  const data = imagesBase64.map(imgB64 => ({
    type: 'pixel',
    format: 'image',
    flavor: 'base64',
    data: imgB64
  }));
  
  try {
    await qz.print(config, data);
    console.log('Impressão pixel enviada com sucesso para:', printerName);
    console.log('Etiquetas enviadas:', imagesBase64.length);
    
    // Disconnect after printing to release printer
    setTimeout(async () => {
      try {
        await disconnectQZ();
        console.log('QZ Tray desconectado após impressão');
      } catch (e) {
        console.warn('Aviso ao desconectar QZ Tray:', e);
      }
    }, 2000);
    
    return true;
  } catch (err) {
    console.error('Erro na impressão pixel:', err);
    throw new Error(`Erro ao imprimir: ${err.message}`);
  }
}

/**
 * Send a test page via pixel printing (image through driver).
 */
export async function printTestPage(printerName, imageBase64, labelSize = { width: 40, height: 25 }, dpi = 203) {
  await connectQZ();
  
  const config = qz.configs.create(printerName, {
    size: { width: labelSize.width / 25.4, height: labelSize.height / 25.4 },
    units: 'in',
    colorType: 'grayscale',
    interpolation: 'nearest-neighbor',
    scaleContent: true,
    rasterize: true,
    density: dpi,
    margins: { top: 0, right: 0, bottom: 0, left: 0 }
  });
  
  const data = [{
    type: 'pixel',
    format: 'image',
    flavor: 'base64',
    data: imageBase64
  }];
  
  try {
    await qz.print(config, data);
    console.log('Teste pixel enviado para:', printerName);
    return true;
  } catch (err) {
    console.error('Erro no teste de impressão:', err);
    throw new Error(`Erro no teste: ${err.message}`);
  }
}

/**
 * RAW PRINTING - Sends raw TSPL commands directly to printer.
 * Requires "Generic / Text Only" driver on Windows.
 * Use printPixelLabels() instead for most cases.
 */
export async function printRawTSPL(printerName, tsplCommands) {
  await connectQZ();
  
  const config = qz.configs.create(printerName, { encoding: 'UTF-8' });
  
  let dataArray;
  if (typeof tsplCommands === 'string') {
    dataArray = tsplCommands
      .split(/\r?\n/)
      .filter(line => line.trim().length > 0)
      .map(line => line + '\r\n');
  } else if (Array.isArray(tsplCommands)) {
    dataArray = tsplCommands.map(cmd => cmd.endsWith('\n') ? cmd : cmd + '\r\n');
  } else {
    dataArray = [String(tsplCommands) + '\r\n'];
  }
  
  try {
    await qz.print(config, dataArray);
    console.log('Impressão raw TSPL enviada para:', printerName);
    
    setTimeout(async () => {
      try {
        await disconnectQZ();
      } catch (e) {
        console.warn('Aviso ao desconectar QZ Tray:', e);
      }
    }, 2000);
    
    return true;
  } catch (err) {
    console.error('Erro na impressão raw:', err);
    throw new Error(`Erro ao imprimir: ${err.message}`);
  }
}

/**
 * RECEIPT PRINTING - Sends ESC/POS commands to non-fiscal thermal receipt printer.
 * Works with Epson TM-T20, Bematech MP-4200, Daruma, Elgin, etc.
 * 
 * @param {string} printerName - Exact printer name
 * @param {string} receiptText - Formatted receipt text (plain text, will be sent with ESC/POS commands)
 */
export async function printReceipt(printerName, receiptText) {
  await connectQZ();
  
  const config = qz.configs.create(printerName);
  
  // ESC/POS commands
  const ESC = '\x1B';
  const GS = '\x1D';
  
  const data = [
    ESC + '@',                    // Initialize printer
    ESC + 'a' + '\x01',          // Center align
    ESC + 'E' + '\x01',          // Bold on
    ESC + '!' + '\x10',          // Double height
    'STERISUL CME\n',
    'RECEPCAO DE MATERIAIS\n',
    ESC + '!' + '\x00',          // Normal size
    ESC + 'E' + '\x00',          // Bold off
    '================================\n',
    ESC + 'a' + '\x00',          // Left align
    receiptText + '\n',
    '================================\n',
    ESC + 'a' + '\x01',          // Center align
    'Obrigado pela preferencia!\n',
    'www.sterisul.com.br\n',
    '\n\n\n',
    GS + 'V' + '\x00',           // Full paper cut
  ];
  
  try {
    await qz.print(config, data);
    console.log('Recibo enviado para:', printerName);
    return true;
  } catch (err) {
    console.error('Erro ao imprimir recibo:', err);
    throw new Error(`Erro ao imprimir recibo: ${err.message}`);
  }
}

/**
 * Find thermal receipt printer (non-fiscal)
 * Looks for common receipt printer names
 */
export async function findReceiptPrinter() {
  await connectQZ();
  
  try {
    const allPrinters = await qz.printers.find();
    const receiptPrinter = allPrinters.find(p => {
      const lower = p.toLowerCase();
      return lower.includes('epson') ||
        lower.includes('bematech') ||
        lower.includes('daruma') ||
        lower.includes('elgin') ||
        lower.includes('tm-t') ||
        lower.includes('mp-') ||
        lower.includes('termica') ||
        lower.includes('thermal') ||
        lower.includes('receipt') ||
        lower.includes('pos') ||
        lower.includes('generic');
    });
    
    return receiptPrinter || null;
  } catch (err) {
    console.error('Erro ao buscar impressora térmica:', err);
    throw err;
  }
}

// Alias para compatibilidade
export const printRawPPLA = printRawTSPL;

/**
 * Get list of all available printers
 */
export async function getAllPrinters() {
  await connectQZ();
  return await qz.printers.find();
}

/**
 * Check if QZ Tray is available
 */
export function isQZActive() {
  return connected && qz.websocket.isActive();
}
